import { GoSuiteApp } from "go-app/services/types";
import { MoneyApi } from "go-core/api/types";
import { AddressApi } from "go-core/types";
import { CustomFieldEntityApi } from "go-segment/services/types";
import { PartnerApi, PartnerProvisionApi } from "./Partner/partnerTypes";
import { SubscriptionLicenseType } from "./Subscription/subscriptionTypes";

export interface ConfigApi {
	apps: GoSuiteApp[];
}

export interface OrganizationApi {
	id: number;
	company_id: string;
	name: string;
	organization_id: number;
	organization_name: string;
	applications: OrganizationApplicationApi[];
	company_email: string;
	company_phone_number: string;
	company_other: string;
	company_country: string;
	company_zip_code: string;
	company_city: string;
	company_street: string;
	company_build_nr: string;
	company_flat_nr: string;
	company_name: string;
	category: string;
	partners: PartnerApi[];
	company_tax_id_no: string;
	tags: string[];
	created_at: string;
	activated_at: string;
	last_used_at: string;
	license_expires_at: string;
	license_blocked_at: string;
	license_type: SubscriptionLicenseType;
	status: string;
	contact_person: string;
	total_subscription_value: MoneyApi;
	provisions?: PartnerProvisionApi[];
	default_provisions?: PartnerProvisionApi[];
	gopos_last_used_at: string;
	gopos_created_at: string;
	gopos_license_expires_at: string;
	gopos_activated_at: string;
	accept_privacy: boolean;
	accept_rules: boolean;
	accept_terms: boolean;
	place: OrganizationPlaceApi;
	accesses?: OrganizationApplicationAccessApi[];
	custom_fields: CustomFieldEntityApi[];
	partner_kam: PartnerApi[];
	partner_cs: PartnerApi[];
	partner_top: PartnerApi[];
	partner_sale: PartnerApi[];
	partner_reseller: PartnerApi[];
	partner_affiliate: PartnerApi[];
	reason_of_removal: string;
	mrr: MoneyApi;
	mrr_sum: MoneyApi;
	migrated_at: string;
	first_paid_at: string;
	cancellation_reason: string;
	payment_date_due: string;
	expected_payment_date: string;
	not_paid_invoices_amount: MoneyApi;
	not_paid_invoices_count: number;
	payment_count: number;
	subscription_count: number;
	last_sale_at: string;
	first_sale_at: string;
	last_activity_at: string;
	first_activity_at: string;
	integrations: OrganizationIntegrationApi[];
	license_expires_at_year: string;
	license_expires_at_month: string;
	license_expires_at_day: string;
	type: string;
	intercom_url?: string;
	cancelled_at: string;
	cancellation_reason_details: string;
	outdated_terminals_count: number;
	test_version_terminals_count: number;
	timezone?: string;
	currency?: string;
	products: string[];
	potential_products: string[];
	potential_points_count: number;
	points_count: number;
	potential_products_count: number;
	products_count: number;
	points_percent: number;
	products_percent: number;
	sub_partner?: PartnerApi;
	partner?: PartnerApi;
	abused_access: number;
	stats: OrganizationStatsApi;
	features: OrganizationFeatureApi[];
}

export interface OrganizationIntegrationApi {
	name: string;
	url: string;
}

export interface OrganizationStatsApi {
	feature_potential_points: number;
	feature_potential_count: number;
	feature_points: number;
	feature_count: number;
	feature_points_percent: number;
	feature_percent: number;
}

export interface OrganizationPlaceApi {
	facebook: string;
	instagram: string;
	website: string;
	linkedin: string;
	city: string;
	street: string;
	build_nr: string;
	flat_nr: string;
	country: string;
	zip_code: string;
	location: CoordinatesApi;
	email: string;
	phone_number: string;
}

export interface CoordinatesApi {
	latitude: number;
	longitude: number;
}

export interface ContactDetailsApi {
	phone_number: string;
	email: string;
	others: string;
}

export interface OrganizationMeApi {
	id: number;
	name: string;
	company_id: string;
	applications: OrganizationApplicationApi[];
	accept_rules: boolean;
	accept_privacy: boolean;
	accept_terms: boolean;
	category: string;
	address: AddressApi;
	contact: ContactDetailsApi;
	tax_id_no: string;
	company_name: string;
	intercom_company_id: string;
	intercom_id: string;
	is_admin: boolean;
}

export interface AcceptOrganizationTermsSecurityApi {
	accept_terms: boolean;
	accept_rules: boolean;
	accept_privacy: boolean;
	support_access: boolean;
}

export interface UserApi {
	id: number;
	email: string;
	name: string;
	roles: Array<UserRole>;
	organizations: OrganizationApi[];
	phone_number: string;
	type: string;
	intercom_url: string;
	tags: string[];
}
export enum UserRole {
	"ADMIN" = "ADMIN",
	"USER" = "USER",
	"SUPPORT" = "SUPPORT",
	"IT_SUPPORT" = "IT_SUPPORT",
	"POSNET" = "POSNET",
	"OFFICE" = "OFFICE",
	"SALE" = "SALE",
	"SALE_TL" = "SALE_TL",
	"CS_TL" = "CS_TL",
	"CSV_TL" = "CSV_TL",
}

export interface OrganizationApplicationApi {
	id: number;
	slug: string;
	name: string;
	status: string;
	properties?: OrganizationApplicationPropertyApi[];
	accesses?: OrganizationApplicationAccessApi[];
	roles?: OrganizationApplicationRoleApi[];
	created_at: string;
	last_used_at: string;
	activated_at: string;
	company_id: string;
	license_expires_at: string;
	license_created_at: string;
	license_type: SubscriptionLicenseType;
}

export interface OrganizationApplicationPropertyApi {
	type: string;
	value: string;
}

export interface OrganizationApplicationRoleApi {
	uid: string;
	name: string;
	permissions: string[];
}

export interface OrganizationApplicationAccessApi {
	type: string;
	value?: string;
	custom_value: string;
}

export interface OrganizationMeApi extends OrganizationApi {
	permissions: string[];
	intercom: string;
	company_id: string;
}

export interface ImageLinkApi {
	default_link?: string;
	small?: string;
	default_as_base64?: string;
	small_as_base64?: string;
	default: string;
}

export interface ImageApi {
	image_data?: string;
	type?: string;
}

export interface PriceConfiguratorContentApi {
	family_id: string;
	plans: PriceConfiguratorPlanApi[];
	priority: number;
	description: string;
	name: string;
}

export interface PriceConfiguratorPlanApi {
	plan_id: string;
	billing_periods: string[];
	addons: PriceConfiguratorAddonApi[];
	name: string;
	description: string;
	edit_mode: string;
	license_level: string;
}

export interface PriceConfiguratorAddonApi {
	addon_id: string;
	details: PriceConfiguratorAddonDetailsApi;
	monthly_sub_details: PriceConfiguratorAddonDetailsApi;
	name: string;
	priority: number;
	description: string;
	terms?: PriceConfiguratorAddonTermsApi;
}

export interface PriceConfiguratorAddonTermsApi {
	name: string;
	url: string;
}

export interface PriceConfiguratorAddonDetailsApi {
	quantity: number;
	requirement: "MANDATORY" | "RECOMMENDED" | "OPTIONAL";
	value_type: "NUMBER" | "BOOLEAN";
	min_value?: number;
	required: boolean;
}

export interface CustomerApi {
	external_id: string;
	name: string;
	status: string;
}

export interface CreatePartnerOrganizationApi {
	name: string;
	category: string;
	email: string;
	address?: AddressApi;
	taxIdNo?: string;
}

export interface LoanApi {
	id: number;
	partner: PartnerApi;
	organization: OrganizationApi;
	items: LoanItemApi[];
	status: string;
	attachments: FileAttachmentApi[];
	created_by: string;
	opened_by: string;
	closed_by: string;
	created_at: string;
	opened_at: string;
	closed_at: string;
	reference_id: string;
	organization_id?: number;
	type: "GOPOS" | "POSNET";
	license_level: LoanLicenseType;
	start_at: string;
	acceptation_status: string;
	signing_status: string;
	confirmed_by: string;
	confirmed_at: string;
	cancellation_reason: string;
	cancelled_at: string;
	terminated_at?: string;
	partner_id?: number;
}

export enum LoanLicenseType {
	"PRO" = "PRO",
	"BASIC" = "BASIC",
}

export enum LoanPreviewMode {
	CLIENT = "CLIENT",
}

export interface LoanItemApi {
	serial_id: string;
	name: string;
}

export interface FileAttachmentApi {
	id: string;
	name: string;
	url: string;
	created_at: string;
	content: string;
	status: string;
}

export interface PayoutInvoiceApi {
	id: string;
	name: string;
	url: string;
	created_at: string;
	status: string;
	number: string;
	total_price: MoneyApi;
	issued_at: string;
	partner_id: number;
	partner: PartnerApi;
}

export interface PayoutInvoiceFormProps {
	total_price: MoneyApi;
	number: string;
	issued_at: string;
}

export interface CreateLoanApi {
	organization_id: number;
	reference_id: string;
	partner_id: number;
	items: LoanItemApi[];
	type: string;
	license_level: LoanLicenseType;
}

export interface RemoveOrganizationApi {
	password: string;
	reason: string;
}

export interface GetCoordinatesApi {
	city?: string;
	build_nr?: string;
	street?: string;
}

export interface OrganizationsDashboardApi {
	cancelled: number;
	cancelled_in_last30_days: number;
	cancelled_in_last30_days_without_reason: number;
	cancelled_without_reason: number;
	license_blocked_in7_days: number;
	license_expires_in7_days: number;
	license_blocked: number;
	license_active_without_sale_for7_days: number;
}

export interface SearchSelectApi {
	id: string | number;
	label: string;
}

export interface AppVersionSearchSelectApi extends SearchSelectApi {
	status: string;
}

export interface OrganizationFormProps {
	name: string;
	contact_person: string;
	tags: string[];
	address: AddressApi;
	contact: ContactDetailsApi;
	tax_id_no: string;
	company_name: string;
	place: OrganizationPlaceApi;
	custom_fields: CustomFieldEntityApi[];
}

export interface OrganizationProductFormApi {
	products: OrganizationProductFormProductApi[];
}

export interface OrganizationProductFormProductApi {
	partner_id: number | null;
	partner: PartnerApi | null;
	value: boolean;
	name: string;
}

export interface OrganizationProductApi {
	id: number;
	name: string;
	points: number;
	possible_points: number;
	partner?: PartnerApi;
	partner_id?: number;
	mrr: number;
	lifetime_value: number;
	subscription_value: boolean;
	last_used_at?: string;
}

export interface OrganizationProductPerCategoryApi {
	category: "License" | "Rent";
	dynamic_value: boolean;
	id: number;
	lifetime_value: 0;
	manual_value: boolean;
	mrr: number;
	name: string;
	subscription_value: boolean;
	products: OrganizationProductApi[];
	mrr_percent: number;
	points_count: number;
	points_percent: number;
	potential_mrr: number;
	potential_points_count: number;
	potential_products_count: number;
	products_count: number;
	products_percent: number;
}

export enum OrganizationTypeApi {
	PROD = "PROD",
	TEST = "TEST",
	DEMO = "DEMO",
}

export interface OrganizationFeatureApi {
	id: number;
	name: string;
	product_name: string;
	notes: string;
	enabled: boolean;
	potential: boolean;
	used: boolean;
	points: number;
	potential_points: number;
	sold_by_partner?: PartnerApi;
	sold_by_partner_id?: number;
	activated_by_partner?: PartnerApi;
	activated_by_partner_id?: number;
	last_used_at?: string;
	first_used_at?: string;
	last_usage: number;
	subscription: boolean;
}

export interface OrganizationFeatureFormApi {
	features: OrganizationFeatureFormFeatureApi[];
}

export interface OrganizationFeatureFormFeatureApi {
	sold_by_partner_id: number | null;
	activated_by_partner_id: number | null;
	sold_by: PartnerApi | null;
	activated_by: PartnerApi | null;
	potential: boolean;
	name: string;
	notes: string;
}
